import React, { FunctionComponent } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { AuthProvider } from 'services/authentication';

import SectionError from 'routes/errors/section';
import RootError from 'routes/errors/root';
import Home from './routes/home';
import Root from './routes/root';

import Dashboards from './routes/dashboards/index';
import ShowDashboard from './routes/dashboards/show';
import EditDashboard from './routes/dashboards/edit';
import CreateDashboard from 'routes/dashboards/create';
import ShowAdmin from 'routes/admin/index';

import { loader as dashboardLoader } from './routes/dashboards/show';
import { loader as userLoader } from './routes/admin/users/edit';

import RequireAuth from './components/RequireAuth';
import RequireAdmin from 'components/RequireAdmin';
import Splash from './components/Splash';

import './App.scss';

import AdminCreateUser from 'routes/admin/users/create';
import AdminEditUser from 'routes/admin/users/edit';
import AdminEditDashboard from 'routes/admin/dashboards/edit';
import AdminCreateDashboard from 'routes/admin/dashboards/create';
import ClearDashboardInfo from 'components/ClearDashboardInfo';

// const showSplash = process.env.NODE_ENV !== 'development';
const showSplash = false;

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            staleTime: 1000 * 10,
            retry: 0,
            // This is set to true to allow errors to bubble up to React Router errorElements
            useErrorBoundary: true,
        },
    },
});

export const routes = [
    {
        path: '/',
        element: showSplash ? (
            <Splash />
        ) : (
            <RequireAuth>
                <Root />
            </RequireAuth>
        ),
        errorElement: <RootError />,
        children: [
            {
                errorElement: <SectionError />,
                children: [
                    {
                        index: true,
                        element: <Home />,
                    },
                    {
                        path: 'dashboards',
                        element: <Dashboards />,
                    },
                    {
                        path: 'dashboards/new',
                        element: <CreateDashboard />,
                    },
                    {
                        path: 'dashboards/:dashboardId',
                        element: <ShowDashboard />,
                        loader: dashboardLoader(queryClient),
                    },
                    {
                        path: 'dashboards/:dashboardId/edit',
                        element: <EditDashboard />,
                        loader: dashboardLoader(queryClient),
                    },
                    {
                        path: 'admin',
                        element: (
                            <ClearDashboardInfo>
                                <RequireAdmin>
                                    <Outlet />
                                </RequireAdmin>
                            </ClearDashboardInfo>
                        ),
                        children: [
                            {
                                index: true,
                                element: <ShowAdmin />,
                            },
                            {
                                path: 'dashboards/new',
                                element: <AdminCreateDashboard />,
                            },
                            {
                                path: 'dashboards/:dashboardId/edit',
                                element: <AdminEditDashboard />,
                                loader: dashboardLoader(queryClient),
                            },
                            {
                                path: 'users/new',
                                element: <AdminCreateUser />,
                            },
                            {
                                path: 'users/:userId/edit',
                                element: <AdminEditUser />,
                                loader: userLoader(queryClient),
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

const App: FunctionComponent = () => {
    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <RouterProvider router={createBrowserRouter(routes)} />
            </AuthProvider>
            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    );
};

export default App;
